import { createContext, useContext, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Warning, Info } from "@phosphor-icons/react";

const Ctx = createContext(null);
export function useDialog() { return useContext(Ctx); }

// Global, centered, enterprise-grade confirm/alert dialogs (replaces window.confirm/alert).
//   const { confirm, notify } = useDialog();
//   if (await confirm({ title, message, confirmText, danger })) { ... }
//   await notify("Something happened");  // or notify({ title, message })
export function DialogProvider({ children }) {
  const [state, setState] = useState(null);

  const close = useCallback((val) => {
    setState((s) => { s?.resolve?.(val); return null; });
  }, []);

  const confirm = useCallback((opts) => new Promise((resolve) => {
    setState({ kind: "confirm", title: "Are you sure?", confirmText: "Confirm", cancelText: "Cancel", ...opts, resolve });
  }), []);

  const notify = useCallback((opts) => new Promise((resolve) => {
    const o = typeof opts === "string" ? { message: opts } : opts;
    setState({ kind: "notify", title: "Notice", confirmText: "OK", ...o, resolve });
  }), []);

  const danger = state?.danger;

  return (
    <Ctx.Provider value={{ confirm, notify }}>
      {children}
      <AnimatePresence>
        {state && (
          <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={() => close(state.kind === "confirm" ? false : true)} />
            <motion.div initial={{ scale: 0.95, opacity: 0, y: 8 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
              transition={{ type: "spring", stiffness: 320, damping: 26 }}
              className="relative w-full max-w-sm rounded-2xl border border-[var(--line)] bg-[var(--shell)] p-6 shadow-2xl">
              <div className="mb-3 flex items-center gap-3">
                <div className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${danger ? "bg-neon-pink/15 text-neon-pink" : "bg-neon-cyan/15 text-neon-cyan"}`}>
                  {danger ? <Warning size={22} weight="fill" /> : <Info size={22} weight="fill" />}
                </div>
                <h3 className="text-lg font-extrabold leading-tight text-[var(--fg)]">{state.title}</h3>
              </div>
              {state.message && <p className="text-sm leading-relaxed text-[var(--fg-soft)]">{state.message}</p>}
              <div className="mt-6 flex justify-end gap-2">
                {state.kind === "confirm" && (
                  <button onClick={() => close(false)}
                    className="rounded-xl border border-[var(--line-strong)] px-4 py-2.5 text-sm font-bold text-[var(--fg-soft)] transition hover:bg-white/[0.06]">
                    {state.cancelText}
                  </button>
                )}
                <button onClick={() => close(true)} autoFocus
                  className={`rounded-xl px-5 py-2.5 text-sm font-bold text-black transition hover:brightness-110 ${danger ? "bg-neon-pink shadow-glow-pink" : "bg-neon-cyan shadow-glow-cyan"}`}>
                  {state.confirmText}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Ctx.Provider>
  );
}
